import Modal from '@/components/Modal'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { UserRole } from '@/types/users'
import { useState } from 'react'
import { toast } from 'sonner'
import { useTranslation } from '@/i18n'

type InviteUserModalProps = {
  isOpen: boolean
  onClose: () => void
  onSubmit: (userData: {
    email: string
    role: UserRole
  }) => Promise<void>
}

const InviteUserModal = ({
  isOpen,
  onClose,
  onSubmit,
}: InviteUserModalProps) => {
  const { t } = useTranslation()
  const [formData, setFormData] = useState({
    email: '',
    role: 'member' as UserRole,
  })
  const [isLoading, setIsLoading] = useState(false)

  const roleOptions = [
    { value: 'member' as UserRole, label: t('common:team_modals.roles.member') },
    { value: 'annotator' as UserRole, label: t('common:team_modals.roles.annotator') },
    { value: 'admin' as UserRole, label: t('common:team_modals.roles.admin') },
  ]

  const handleCloseModal = () => {
    onClose()
    setFormData({
      email: '',
      role: 'member',
    })
  }

  const handleSubmit = async () => {
    if (!formData.email.trim()) {
      toast.error(t('common:team_modals.invite.email_required'))
      return
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(formData.email.trim())) {
      toast.error(t('common:team_modals.invite.email_invalid'))
      return
    }

    setIsLoading(true)
    try {
      await onSubmit({
        email: formData.email.trim(),
        role: formData.role,
      })
      handleCloseModal()
    } catch {
      // Error handling is done in the parent component
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleCloseModal}
      description={t('common:team_modals.invite.description')}
      title={t('common:team_modals.invite.title')}
      className="w-[400px]"
      footer={
        <div className="flex gap-3 w-full">
          <Button
            variant="outline"
            onClick={handleCloseModal}
            disabled={isLoading}
            className="flex-1"
          >
            {t('common:team_modals.invite.cancel')}
          </Button>
          <Button onClick={handleSubmit} disabled={isLoading} className="flex-1">
            {isLoading ? t('common:team_modals.invite.inviting') : t('common:team_modals.invite.invite')}
          </Button>
        </div>
      }
    >
      <div className="grid gap-4 py-4">
        {/* Email field (required) */}
        <div className="grid gap-2">
          <Label htmlFor="invite-email">{t('common:team_modals.invite.email_label')}</Label>
          <Input
            id="invite-email"
            value={formData.email}
            type="email"
            placeholder={t('common:team_modals.invite.email_placeholder')}
            onChange={(e) =>
              setFormData({ ...formData, email: e.target.value })
            }
            required
          />
        </div>

        {/* Role field */}
        <div className="grid gap-2">
          <Label htmlFor="invite-role">{t('common:team_modals.invite.role_label')}</Label>
          <Select
            value={formData.role}
            onValueChange={(value: UserRole) =>
              setFormData({ ...formData, role: value })
            }
          >
            <SelectTrigger className="w-full" id="invite-role">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {roleOptions.map(({ value, label }) => (
                <SelectItem key={value} value={value}>
                  {label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
    </Modal>
  )
}

export default InviteUserModal
